/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import com.benasher44.uuid.Uuid
import com.benasher44.uuid.uuidFrom
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import systems.frictionless.impulse.rest.internal.UuidSerializer

/**
 * An assessment encapsulates a group of answers that have been provided by a user.  (Question objects are not stored
 * by the Assessment, although a series of Question objects will be iterated during the administration of an assessment
 * in order to solicit Answer objects from the User).
 *
 * Note that an Assessment doesn't know about its contents.
 *
 * @param id Unique ID for the assessment.
 * @param userId User that this assessment belongs to.
 * @param type The type of assessment.
 * @param createdAt The time the assessment was created.
 * @see User
 * @see Answer
 * @see
 */
@Serializable
data class Assessment(
    @Serializable(with = UuidSerializer::class) val id: Uuid,
    @Serializable(with = UuidSerializer::class) val userId: Uuid,
    val type: Assessment.Type,
    val createdAt: Instant
) {
    @Serializable
    data class Builder(val type: Assessment.Type)

    /**
     * Type of assessment.
     *
     * Currently there is only one type, although this gives some flexibility to change types in the future.
     */
    @Serializable
    data class Type(
        @Serializable(with = UuidSerializer::class) val typeId: Uuid
    ) {
        companion object {
            val IMPULSE = uuidFrom("EF75C9BA-6393-4A2E-86AA-FB0DF40B71B3")
        }
    }

    @Serializable
    enum class State {
        DemographicsOverview,
        Demographics,
        QuestionsOverview,
        Questions,
        Complete
    }
}
